import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import PendingTransferPicker from '@/features/dashboard/rawBankTransactions/components/PendingTransferPicker.tsx';
import useMatchExpenseToBankTransaction from '@/features/dashboard/expenses/hooks/useMatchExpenseToBankTransaction.ts';
import useGetExpense from '@/features/dashboard/expenses/hooks/useGetExpense.ts';
import MatchExpenseModalSkeleton from '@/features/dashboard/expenses/components/MatchExpenseModalSkeleton.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import { remainingToPay } from '@/features/dashboard/expenses/types/TourExpenseDto.utils.ts';
import { optionalPositiveNumberSchema } from '@/schemas/optionalNumberSchema.ts';
import { formatPrice } from '@/utils/formatters.ts';

const matchExpenseSchema = z.object({
  rawTransactionId: z.string().min(1, 'Wybierz przelew z wyciągu'),
  allocatedAmount: optionalPositiveNumberSchema('Kwota musi być większa niż 0'),
});

type MatchExpenseFormData = z.infer<typeof matchExpenseSchema>;
type MatchExpenseInputData = z.input<typeof matchExpenseSchema>;

interface MatchExpenseModalProps {
  tourId: string;
  expenseId: string;
  onClose: () => void;
}

const MatchExpenseModal = ({
  tourId,
  expenseId,
  onClose,
}: MatchExpenseModalProps) => {
  const matchMutation = useMatchExpenseToBankTransaction(tourId);
  const {
    data: expense,
    isLoading,
    isError,
  } = useGetExpense(tourId, expenseId);

  const methods = useForm<MatchExpenseInputData>({
    resolver: zodResolver(matchExpenseSchema),
    defaultValues: { rawTransactionId: '', allocatedAmount: undefined },
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const remaining = expense ? remainingToPay(expense) : 0;

  const onSubmit = (data: MatchExpenseFormData) => {
    if (!expense) return;
    const amount = data.allocatedAmount;

    matchMutation.mutate(
      {
        expenseId,
        request: {
          rawTransactionId: data.rawTransactionId,
          // Kwotę podaje się dodatnią, znak odwraca backend.
          allocatedAmount:
            amount === undefined || Number.isNaN(amount)
              ? undefined
              : { amount, currency: expense.amount.currency },
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} isDirty={isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            title="Dopasuj do transakcji bankowej"
            description={expense?.description ?? undefined}
            onClose={onClose}
          />
          <ModalContent>
            {isLoading ? (
              <MatchExpenseModalSkeleton />
            ) : isError || !expense ? (
              <LoadError subject="wydatku" />
            ) : (
              <>
                <div className="rounded-md bg-neutral-50 p-3">
                  <InfoCardList className="flex-1 space-y-1">
                    <InfoCardRow layout="columns" label="Kwota faktury">
                      {formatPrice(
                        expense.amount.amount,
                        expense.amount.currency,
                      )}
                    </InfoCardRow>
                    <InfoCardRow layout="columns" label="Już zapłacono">
                      {formatPrice(expense.paidAmount, expense.amount.currency)}
                    </InfoCardRow>
                    <InfoCardRow layout="columns" label="Pozostało">
                      {formatPrice(remaining, expense.amount.currency)}
                    </InfoCardRow>
                  </InfoCardList>
                </div>

                <Controller
                  control={control}
                  name="rawTransactionId"
                  render={({ field }) => (
                    <PendingTransferPicker
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.rawTransactionId?.message}
                    />
                  )}
                />

                <ValueInput
                  label="Kwota do wpięcia"
                  register={register('allocatedAmount', {
                    valueAsNumber: true,
                  })}
                  placeholder="Cały przelew"
                  type="number"
                  step={0.01}
                  error={errors.allocatedAmount?.message}
                  sufix={expense.amount.currency}
                  hint="Zostaw puste, żeby wpiąć cały przelew. Przelew większy niż pozostała kwota jest odrzucany. Resztę przelewu rozdysponujesz później na liście transakcji, edytując jego alokacje."
                />
              </>
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={matchMutation.isPending}
              disabled={isLoading || !expense}
            >
              Dopasuj
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default MatchExpenseModal;
